/// <amd-module name="Wit/UI/DataProviders/WorkItemTrackingDataProvider" />
import { IVssPageContext } from "VSS/Platform/Context";
import { WorkItem, WorkItemExpand, WorkItemTypeColorAndIcon } from "Wit/Clients/Wit/Contracts/WorkItemTracking";
import { IWorkItemTrackingRestClient } from "Wit/Clients/Wit/RestClient/WorkItemTracking.Types";
import { WorkItemStateAndColor } from "Wit/UI/WorkItemState";
import { Wiql, WorkItemId } from "../Client";
import { IPagedWorkItemsList, PagedWorkItemsList } from "../PagedWorkItemsList";
import { IWorkItemTrackingDataProvider } from "./IWorkItemTrackingDataProvider";
import {
    WorkItemTypeIconMetadataServiceId,
    IWorkItemTypeIconMetadataService,
    WorkItemStateColorMetadataServiceId,
    IWorkItemStateColorMetadataService
} from "Wit/UI/Services";

export const DEFAULT_WORKITEM_PAGE_SIZE = 200;

/**
 * Default implementation of IWorkItemTrackingDataProvider
 */
export class WorkItemTrackingDataProvider implements IWorkItemTrackingDataProvider {
    private _cachedWorkItemPromises: { [id: number]: Promise<WorkItem> } = {};
    private _workItemsCache: { [id: number]: WorkItem } = {};

    constructor(
        private readonly _context: IVssPageContext,
        private readonly _defaultProjectIdOrName?: string,
        private readonly _fieldRefNames?: string[]
    ) {}

    public getWorkItemTypeColorAndIcon(
        projectIdOrName: string,
        workItemTypeName: string
    ): Promise<Readonly<WorkItemTypeColorAndIcon>> | Readonly<WorkItemTypeColorAndIcon> {
        const WorkItemTypeIconMetadataService = this._context.getService<IWorkItemTypeIconMetadataService>(WorkItemTypeIconMetadataServiceId);
        return WorkItemTypeIconMetadataService.getWorkItemIcon(projectIdOrName, workItemTypeName);
    }

    public getWorkItemStateAndColor(
        projectIdOrName: string,
        workItemTypeName: string,
        stateName: string
    ): Promise<Readonly<WorkItemStateAndColor>> | Readonly<WorkItemStateAndColor> {
        const workItemStateColorMetadataService = this._context.getService<IWorkItemStateColorMetadataService>(WorkItemStateColorMetadataServiceId);
        return workItemStateColorMetadataService.getWorkItemStateColor(projectIdOrName, workItemTypeName, stateName);
    }

    public async getWorkItem(id: WorkItemId): Promise<Readonly<WorkItem> | null> {
        if (this._workItemsCache[id]) {
            return this._workItemsCache[id];
        }

        try {
            if (!this._cachedWorkItemPromises[id]) {
                const client = this._context.getRestClient<IWorkItemTrackingRestClient>("IWorkItemTrackingRestClient");
                this._cachedWorkItemPromises[id] = client.getWorkItem(id, this._defaultProjectIdOrName, undefined, undefined, WorkItemExpand.All);
            }
            this._workItemsCache[id] = await this._cachedWorkItemPromises[id];
            // Remove the cached promise before returns.
            delete this._cachedWorkItemPromises[id];
            return this._workItemsCache[id] || null;
        } catch {
            return null;
        }
    }

    public getPagedWorkItemsList(source: WorkItemId[] | WorkItem[] | Wiql, pageSize: number = DEFAULT_WORKITEM_PAGE_SIZE): IPagedWorkItemsList {
        return new PagedWorkItemsList(this._context, source, this._workItemsCache, pageSize, this._defaultProjectIdOrName, this._fieldRefNames);
    }

    public getFieldReferenceNames(): ReadonlyArray<string> | undefined {
        return this._fieldRefNames;
    }

    private _getProjectWorkItemTypeKey(projectIdOrName: string, workItemType: string): string {
        return `${projectIdOrName}_${workItemType}`.toLocaleLowerCase();
    }
}
